/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { FormProvider, useForm } from "react-hook-form";
import { Input, Text, theme } from "@nordcloud/gnui";
import { FlexContainer } from "~/components";
import {
  BasicInfoEditModeType,
  BasicInfoLoadingType,
  Customer,
  Values,
} from "~/components/BasicCustomerInfo/types";
import { StyledErrorText } from "~/components/Contract/ContractDurationDates";
import { EditButton } from "~/components/InlineEdit";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { SidebarFormField } from "~/components/SidebarForm/SidebarFormField";

type EditBusinessHoursProps = {
  item: Customer;
  onSubmit: (values: Customer) => Promise<void>;
  isLoading: BasicInfoLoadingType;
  setIsLoading: React.Dispatch<React.SetStateAction<BasicInfoLoadingType>>;
  isEditMode: BasicInfoEditModeType;
  setIsEditMode: React.Dispatch<React.SetStateAction<BasicInfoEditModeType>>;
  canEdit?: boolean;
};

const checkBusinessHours = (start: string | null, end: string | null) => {
  if (start === "" || end === "") {
    return "Error";
  }

  if (start && end && start > end) {
    return "Error";
  }

  return null;
};

function EditBusinessHours({
  item,
  onSubmit,
  isLoading,
  setIsLoading,
  isEditMode,
  setIsEditMode,
  canEdit = true,
}: EditBusinessHoursProps) {
  const [error, setError] = React.useState<null | string>(null);
  const methods = useForm();

  const onEdit = async (values: Values) => {
    if (checkBusinessHours(values.start, values.end)) {
      setError("Please input correct hours");

      return;
    }
    setIsLoading((prevState) => ({ ...prevState, businessHours: true }));
    setError(null);

    const organizeValues = {
      ...item,
      info: {
        ...item.info,
        businessHours: { start: values.start, end: values.end },
      },
    };

    await onSubmit(organizeValues);
  };

  const setEditMode = (value: boolean) => {
    setIsEditMode((prevState) => ({
      ...prevState,
      businessHours: value,
    }));
  };

  const businessHours = item.info.businessHours;

  return (
    <FormProvider {...methods}>
      <FlexContainer>
        <Text
          color={theme.color.text.text02}
          m="0"
          mr={theme.spacing.spacing01}
        >
          Start:
        </Text>
        <Text as="div" m="0" data-cy="start-hours">
          {businessHours?.start ? businessHours?.start : "-"}
        </Text>
        <Text
          as="div"
          m="0"
          mr={theme.spacing.spacing02}
          ml={theme.spacing.spacing02}
        >
          -
        </Text>
        <Text
          color={theme.color.text.text02}
          m="0"
          mr={theme.spacing.spacing01}
        >
          End:
        </Text>
        <Text as="div" m="0" data-cy="end-hours">
          {businessHours?.end ? businessHours?.end : "-"}
        </Text>
        {canEdit && <EditButton onClick={() => setEditMode(true)} />}
      </FlexContainer>

      <SidebarForm
        title="Business details"
        open={isEditMode.businessHours}
        setOpen={setEditMode}
        formMethods={methods}
        submit={onEdit}
      >
        <Row>
          <Col>
            <SidebarFormField label="Start" tooltip="Start business hours">
              <Input
                type="time"
                data-cy="text-input"
                placeholder="Start"
                name="start"
                ref={methods.register}
                defaultValue={businessHours?.start || ""}
              />
            </SidebarFormField>
          </Col>
          <Col>
            <SidebarFormField label="End" tooltip="End business hours">
              <Input
                type="time"
                data-cy="text-input"
                placeholder="End"
                name="end"
                ref={methods.register}
                defaultValue={businessHours?.end || ""}
              />
            </SidebarFormField>
          </Col>
        </Row>

        {error && <StyledErrorText>{error}</StyledErrorText>}

        <SidebarFormButtons
          onCancel={() => setEditMode(false)}
          submitLoading={isLoading.businessHours}
        />
      </SidebarForm>
    </FormProvider>
  );
}

export { EditBusinessHours };
